import { Component, EventEmitter, Input, OnChanges, OnInit, Output } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { finalize } from 'rxjs/operators';
import { Attachment } from '../../../../../../core/models/attachment/attachment';
import { UploadDocumentType } from '../../../../../../core/enum/upload-document-type.enum';
import { PopupService } from '../../../../../../shared/services/popup/popup.service';
import { Role } from '../../../../../../core/enum/role.enum';
import { PDFFileFormat } from '../../../../../../core/services/constant';
import { AccountService } from '../../../../../../core/services/api/account.service';
import { ContainerType } from '../../../../../../core/enum/container-type.enum';
import { ErrorService } from '../../../../../../core/services/error.service';
import { FileValidationService } from '../../../../../../core/services/file-validation.service';
import { FileUploadOption } from '../../../../../../core/models/attachment/file-upload-option';
import { AttachmentInfo } from '../../../../../../core/models/attachment/attachment-info';
import { AttachmentAudience } from '../../../../../../core/enum/attachment-audience.enum';

@Component({
    selector: 'app-general-document-upload',
    templateUrl: './general-document-upload.component.html',
    styleUrls: ['./general-document-upload.component.scss'],
    standalone: false
})
export class GeneralDocumentUploadComponent implements OnInit, OnChanges {
  @Input() attachments?: Attachment[];
  @Input() description?: Attachment[];
  @Input() isHideDelete: boolean = false;
  @Input() fileTypes: string[] = PDFFileFormat;
  @Input() document: Attachment = new Attachment(); 
  @Input() fileSizeLimit: number = 31457280; // 30MB

  @Output() uploadDocument = new EventEmitter();
  @Output() deleteDocument = new EventEmitter();
  @Output() uploadOtherDocument = new EventEmitter();

  form!: UntypedFormGroup;
  showFormError: boolean = false;
  isDragging: boolean = false; 
  isLoading: boolean = false;
  documents!: Attachment[];
  accept!: string;

  Role: typeof Role = Role;
  UploadDocumentType: typeof UploadDocumentType = UploadDocumentType;
  AttachmentAudience: typeof AttachmentAudience = AttachmentAudience;
  audienceOptions = [
    { id: AttachmentAudience.All, labelKey: 'common.all' },
    { id: AttachmentAudience.InvestorOnly, labelKey: 'common.investorOnly' },
  ];

  constructor(
    public activeModal: NgbActiveModal,
    private formBuilder: UntypedFormBuilder,
    private activatedRoute: ActivatedRoute,
    private popupService: PopupService,
    private accountService: AccountService,
    private errorService: ErrorService,
    private fileValidationService: FileValidationService,
  ) {}

  ngOnInit() {
    this.accept = this.fileTypes.join(',');
    this.buildForm();
  }

  ngOnChanges() {
    this.documents = this.attachments || [];
    this.accept = (this.fileTypes || []).join(',');  
  }

  buildForm() {
    this.form = this.formBuilder.group({
      fileName: [this.document?.fileName || '', Validators.required],
      audience: [this.document?.audience ?? null, Validators.required],
    });
  }

  onDragOver(event: DragEvent) {
    event.preventDefault();
    event.stopPropagation();
    this.isDragging = true;
  }

  onDragLeave(event: DragEvent) {
    event.preventDefault();
    event.stopPropagation();
    this.isDragging = false;
  }

  onDrop(event: DragEvent) {
    event.preventDefault();
    event.stopPropagation();
    this.isDragging = false;

    const files = event.dataTransfer?.files;
    if (files && files.length > 0) {
      this.selectFile(files[0]);
    }
  }

  async selectFile(file: File) {
    if (!file) return;

    const uploadOption: FileUploadOption = {
      fileType: this.fileTypes,
      errorMsg: this.fileValidationService.errorMessage(this.fileTypes),
      fileSizeLimit: this.fileSizeLimit,
    };

    const isValid = await this.fileValidationService.validFile(file, uploadOption);
    
    if (isValid) {
      this.document.file = file; 
      
      const currentName = this.form.get('fileName')!.value;
      if (!currentName) {
        this.form.patchValue({
          fileName: this.generateFileName(file.name)
        });
      }
    }
  }

  onFileChange(event: any) {
    const file = event.target.files[0];
    if (file) {
      this.selectFile(file);
    }
  }

  async confirm() {
    this.showFormError = false;

    if (!this.document.file) {
      this.showFormError = true;
      this.popupService.alert('Please select a document first');
      return;
    }

    if (this.form.invalid) {
      this.showFormError = true;
      return;
    }

    this.isLoading = true;

    this.accountService
      .uploadAttachmentPresignedURL(this.document.file, ContainerType.Company)
      .pipe(finalize(() => (this.isLoading = false)))
      .subscribe({
        next: (data: any) => {
          const result = {
            fileName: this.form.value.fileName,
            audience: this.form.value.audience,
            attachmentInfo: new AttachmentInfo(data),
            url: data.url,
            status: 'Pending Review'
          };
          
          this.activeModal.close(result);
        },
        error: (err) => this.errorService.showError(err)
      });
  }

  generateFileName(fileName: string): string {
    if (!fileName) return '';
    return fileName.split('.').slice(0, -1).join('.');
  }

  cancel() {
    this.activeModal.dismiss();
  }
}